/**
 * BB Product Spotlight
 * - WAI-ARIA tabs pattern with arrow-key navigation
 * - Synchronises desktop heading list (with featured/thumbnail swap)
 *   and mobile horizontal tab strip
 * - Smooth, JS-driven tab switching (no reload)
 * - Swipe support on mobile content panel
 */
(function () {
  'use strict';

  var ready = function (fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  };

  function getTabs(section) {
    return Array.prototype.slice.call(
      section.querySelectorAll('[data-bb-spotlight-tab]')
    );
  }

  function getPanels(section) {
    return Array.prototype.slice.call(
      section.querySelectorAll('.bb-spotlight__panel')
    );
  }

  function getDesktopList(section) {
    return section.querySelector('.bb-spotlight__left');
  }

  function reorderDesktopList(section, activeIndex) {
    var list = getDesktopList(section);
    if (!list) return;

    var desktopTabs = Array.prototype.slice.call(
      list.querySelectorAll('.bb-spotlight__heading-tab')
    );

    desktopTabs.forEach(function (tab) {
      var idx = parseInt(tab.getAttribute('data-tab-index'), 10);
      tab.classList.toggle('is-featured', idx === activeIndex);
    });

    /*
     * Move the active tab to the top of the DOM order so
     * `grid-column: 1 / -1` makes it span the full row above
     * the remaining thumbnails (matches the design spec).
     */
    var active = desktopTabs.filter(function (tab) {
      return parseInt(tab.getAttribute('data-tab-index'), 10) === activeIndex;
    })[0];

    if (active && list.firstElementChild !== active) {
      list.insertBefore(active, list.firstElementChild);
    }
  }

  function activate(section, index, opts) {
    opts = opts || {};
    var tabs = getTabs(section);
    var panels = getPanels(section);

    if (!tabs.length || !panels.length) return;

    var max = panels.length - 1;
    if (index < 0) index = max;
    if (index > max) index = 0;

    tabs.forEach(function (tab) {
      var tabIdx = parseInt(tab.getAttribute('data-tab-index'), 10);
      var isActive = tabIdx === index;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    panels.forEach(function (panel) {
      var panelIdx = parseInt(panel.getAttribute('data-panel-index'), 10);
      var isActive = panelIdx === index;
      panel.classList.toggle('is-active', isActive);
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    reorderDesktopList(section, index);

    /* Scroll mobile tab into view so the active one stays visible */
    var mobileTabs = section.querySelector('[data-bb-spotlight-mobile-tabs]');
    if (mobileTabs) {
      var activeMobile = mobileTabs.querySelector(
        '.bb-spotlight__mobile-tab.is-active'
      );
      if (activeMobile && typeof activeMobile.scrollIntoView === 'function') {
        try {
          activeMobile.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        } catch (e) {
          /* old browsers */
          activeMobile.scrollIntoView();
        }
      }
    }

    if (opts.focus) {
      var focusTarget = tabs.filter(function (tab) {
        return (
          parseInt(tab.getAttribute('data-tab-index'), 10) === index &&
          tab.offsetParent !== null
        );
      })[0];
      if (focusTarget) focusTarget.focus();
    }
  }

  /* --------------------------------------------------------- Keyboard */

  function onKeydown(section, e) {
    var key = e.key;
    if (
      key !== 'ArrowLeft' &&
      key !== 'ArrowRight' &&
      key !== 'ArrowUp' &&
      key !== 'ArrowDown' &&
      key !== 'Home' &&
      key !== 'End'
    ) {
      return;
    }

    var current = e.target.closest('[data-bb-spotlight-tab]');
    if (!current) return;

    e.preventDefault();

    var tabs = getTabs(section).filter(function (t) {
      return t.offsetParent !== null;
    });
    var idx = tabs.indexOf(current);
    var max = tabs.length - 1;

    var next = idx;
    if (key === 'ArrowLeft' || key === 'ArrowUp') next = idx <= 0 ? max : idx - 1;
    if (key === 'ArrowRight' || key === 'ArrowDown') next = idx >= max ? 0 : idx + 1;
    if (key === 'Home') next = 0;
    if (key === 'End') next = max;

    var nextTab = tabs[next];
    if (!nextTab) return;
    var nextIndex = parseInt(nextTab.getAttribute('data-tab-index'), 10);
    activate(section, nextIndex, { focus: true });
  }

  /* --------------------------------------------------------- Swipe (mobile) */

  function attachSwipe(section) {
    var right = section.querySelector('.bb-spotlight__right');
    if (!right) return;

    var startX = 0;
    var startY = 0;
    var moved = false;
    var threshold = 50;

    right.addEventListener(
      'touchstart',
      function (e) {
        if (!e.touches || !e.touches.length) return;
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        moved = false;
      },
      { passive: true }
    );

    right.addEventListener(
      'touchmove',
      function () {
        moved = true;
      },
      { passive: true }
    );

    right.addEventListener(
      'touchend',
      function (e) {
        if (!moved || !e.changedTouches || !e.changedTouches.length) return;
        if (window.innerWidth >= 990) return;

        var dx = e.changedTouches[0].clientX - startX;
        var dy = e.changedTouches[0].clientY - startY;
        if (Math.abs(dx) < threshold) return;
        if (Math.abs(dy) > Math.abs(dx)) return;

        var tabs = getTabs(section).filter(function (t) {
          return t.offsetParent !== null;
        });
        if (!tabs.length) return;

        var activeTab = tabs.filter(function (t) {
          return t.classList.contains('is-active');
        })[0];
        var idx = activeTab ? tabs.indexOf(activeTab) : 0;
        var max = tabs.length - 1;

        var nextIdx;
        if (dx < 0) {
          nextIdx = idx >= max ? 0 : idx + 1;
        } else {
          nextIdx = idx <= 0 ? max : idx - 1;
        }
        var nextTab = tabs[nextIdx];
        if (!nextTab) return;
        activate(
          section,
          parseInt(nextTab.getAttribute('data-tab-index'), 10)
        );
      },
      { passive: true }
    );
  }

  /* --------------------------------------------------------- Init */

  function initSection(section) {
    if (section.dataset.bbSpotlightInit === '1') return;
    section.dataset.bbSpotlightInit = '1';

    section.addEventListener('click', function (e) {
      var tab = e.target.closest('[data-bb-spotlight-tab]');
      if (!tab || !section.contains(tab)) return;
      e.preventDefault();
      var idx = parseInt(tab.getAttribute('data-tab-index'), 10);
      if (isNaN(idx)) return;
      activate(section, idx);
    });

    section.addEventListener('keydown', function (e) {
      onKeydown(section, e);
    });

    attachSwipe(section);

    /* Ensure the desktop list is in correct visual order on load */
    reorderDesktopList(section, 0);
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-bb-spotlight]');
    Array.prototype.forEach.call(sections, initSection);
  }

  ready(initAll);

  /* Re-initialise on theme editor events */
  document.addEventListener('shopify:section:load', function (e) {
    var sec = e.target.querySelector('[data-bb-spotlight]');
    if (sec) initSection(sec);
  });

  document.addEventListener('shopify:block:select', function (e) {
    var section = e.target.closest('[data-bb-spotlight]');
    if (!section) return;
    var tab = e.target.querySelector('[data-bb-spotlight-tab]');
    if (tab) {
      var idx = parseInt(tab.getAttribute('data-tab-index'), 10);
      if (!isNaN(idx)) activate(section, idx);
    }
  });
})();
